import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Download, ExternalLink, GitBranch, Search, Split, X } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { useNavigate } from "../router";
import type { ConceptAggregate, ConceptPeriod, Creative, Hypothesis, Verdict } from "@lovmia/shared";
import { conceptPeriods, creativeStateLabels } from "@lovmia/shared";
import { api, formatMoney, formatPercent } from "../api";
import { Button, PageHeader, Select, StatusPill, Tag, VerdictPill } from "../components/Ui";
import { HypothesisControls } from "../components/HypothesisControls";
import { useAppState } from "../state";

interface Concept {
  family: Hypothesis;
  creatives: Creative[];
  metrics: ConceptAggregate | null;
  verdicts: Verdict[];
}

/**
 * Метрики концепта приходят с сервера посчитанными из сумм: hook концепта — это
 * все 3s-досмотры его крео, делённые на все показы.
 *
 * Раньше здесь стояли max hook / min CPA по детям. Это отвечало на вопрос
 * «какое исполнение оказалось лучшим», а выглядело как «так работает концепт»:
 * один удачный крео вытягивал строку гипотезы, и по ней принимались решения.
 * Лучшее исполнение никуда не делось — оно во второй строке ячейки, отдельно и
 * подписанное.
 */
const periodLabels: Record<ConceptPeriod, string> = {
  "7d": "7 дней", "14d": "14 дней", all: "всё время"
};

function toggleIn(source: Set<string>, id: string) {
  const next = new Set(source);
  if (next.has(id)) next.delete(id); else next.add(id);
  return next;
}

export function RegistryPage() {
  const { data } = useAppState();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [state, setState] = useState("all");
  const [period, setPeriod] = useState<ConceptPeriod>("all");
  // Разрез считает сервер: для периода нужны суточные срезы, которых в
  // bootstrap нет и быть не должно.
  const registry = useQuery({
    queryKey: ["registry", period],
    queryFn: () => api<{ concepts: ConceptAggregate[] }>(`/api/registry?period=${period}`),
    staleTime: 15_000
  });
  const metricsByHypothesis = useMemo(
    () => new Map((registry.data?.concepts ?? []).map((row) => [row.hypothesisId, row])),
    [registry.data]
  );
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const [closedIds, setClosedIds] = useState<Set<string>>(new Set());
  const filtering = query.trim() !== "" || state !== "all";

  // Фильтр «сквозь уровни»: совпадение по концепту показывает всех его детей,
  // совпадение по крео поднимает наверх только этого ребёнка.
  const concepts = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return data.hypotheses.reduce<Concept[]>((list, family) => {
      const conceptMatch = !needle || `${family.id} ${family.statement} ${family.pain} ${family.desire} ${family.angle}`.toLowerCase().includes(needle);
      const creatives = family.creatives.filter((creative) => {
        const stateMatch = state === "all" || creative.state === state;
        const textMatch = conceptMatch || `${creative.id} ${creative.adName} ${creative.hook}`.toLowerCase().includes(needle);
        return stateMatch && textMatch;
      });
      const keepEmpty = conceptMatch && state === "all" && !family.creatives.length;
      if (creatives.length || keepEmpty) {
        list.push({
          family,
          creatives,
          metrics: metricsByHypothesis.get(family.id) ?? null,
          verdicts: [...new Set(creatives.map((creative) => creative.verdict).filter((value): value is Verdict => value != null))]
        });
      }
      return list;
    }, []);
  }, [data.hypotheses, query, state, metricsByHypothesis]);

  const isOpen = (id: string) => filtering ? !closedIds.has(id) : openIds.has(id);
  const toggle = (id: string) => filtering ? setClosedIds((prev) => toggleIn(prev, id)) : setOpenIds((prev) => toggleIn(prev, id));
  const allOpen = concepts.length > 0 && concepts.every((concept) => isOpen(concept.family.id));
  const toggleAll = () => {
    if (filtering) setClosedIds(allOpen ? new Set(concepts.map((concept) => concept.family.id)) : new Set());
    else setOpenIds(allOpen ? new Set() : new Set(concepts.map((concept) => concept.family.id)));
  };

  const totalCreatives = concepts.reduce((sum, concept) => sum + concept.creatives.length, 0);

  const exportCsv = () => {
    // Числители едут вместе с рейтами: без них выгрузку нельзя пересобрать в
    // концепт — из долей сумму не соберёшь, и цифры в Excel разойдутся с экраном.
    const header = ["hypothesis", "statement", "pain", "creative", "producer", "state", "verdict",
      "spend", "impressions", "clicks", "video_views_3s", "video_thruplays", "real_subscriptions",
      "hook_rate", "hold_rate", "ctr", "cpa", "delivery_status", "ad_name"];
    const lines = [header, ...concepts.flatMap((concept) => concept.creatives.map((creative) => [
      concept.family.id, concept.family.statement, concept.family.pain,
      creative.id, creative.producer, creative.state, creative.verdict ?? "",
      creative.metrics.spend, creative.metrics.impressions, creative.clicks,
      creative.videoViews3s, creative.videoThruplays, creative.realSubscriptions,
      creative.metrics.hookRate, creative.metrics.holdRate,
      creative.metrics.ctr, creative.metrics.cpa ?? "", creative.deliveryStatus ?? "", creative.adName
    ]))];
    const blob = new Blob([lines.map((line) => line.map((value) => `"${String(value).replaceAll('"', '""')}"`).join(",")).join("\n")], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "lovmia-concepts.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return <>
    <PageHeader
      eyebrow="source of truth"
      title="Реестр по концептам"
      description="Гипотеза с агрегатами — сверху, исполнения раскрываются внутри неё. Строка концепта не теряется, даже когда крео разъехались по состояниям."
      actions={<Button onClick={exportCsv}><Download size={15} /> CSV</Button>}
    />
    <div className="registry-toolbar">
      <div className="search-box">
        <Search size={15} />
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="H-041, утверждение, ad name или боль" />
        {query && <button onClick={() => setQuery("")} aria-label="Очистить поиск"><X size={13} /></button>}
      </div>
      <Select value={state} onChange={(event) => setState(event.target.value)} aria-label="Состояние крео">
        <option value="all">Все состояния</option>
        {Object.entries(creativeStateLabels).map(([value, label]) => <option value={value} key={value}>{label}</option>)}
      </Select>
      <Select value={period} onChange={(event) => setPeriod(event.target.value as ConceptPeriod)} aria-label="Период">
        {conceptPeriods.map((value) => <option value={value} key={value}>{periodLabels[value]}</option>)}
      </Select>
      <Button onClick={toggleAll}>{allOpen ? "Свернуть все" : "Раскрыть все"}</Button>
      <span>{concepts.length} концептов · {totalCreatives} крео</span>
    </div>
    <div className="table-wrap">
      <table className="registry-table registry-table--tree">
        <thead>
          <tr>
            <th>Концепт</th>
            <th>Утверждение и теги</th>
            <th>Состояние</th>
            <th>Вердикт</th>
            <th title="У концепта — сумма по исполнениям">Spend <i>Σ</i></th>
            <th title="У концепта — все 3s-досмотры делённые на все показы; ниже — лучшее исполнение">Hook <i>Σ</i></th>
            <th title="У концепта — все thruplay'и делённые на все показы">Hold <i>Σ</i></th>
            <th title="У концепта — все клики делённые на все показы">CTR <i>Σ</i></th>
            <th title="У концепта — спенд делённый на все подписки; ниже — лучшее исполнение">CPS <i>Σ</i></th>
            <th>Ad name</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {concepts.map((concept) => {
            const open = isOpen(concept.family.id);
            const { family } = concept;
            return <Fragment key={family.id}>
              <tr className={`concept-row ${open ? "concept-row--open" : ""}`}>
                <td>
                  <button
                    className="row-toggle"
                    onClick={() => toggle(family.id)}
                    aria-expanded={open}
                    disabled={!concept.creatives.length}
                    aria-label={`${open ? "Свернуть" : "Раскрыть"} концепт ${family.id}`}
                  >
                    <ChevronRight size={14} className={open ? "row-toggle__icon row-toggle__icon--open" : "row-toggle__icon"} />
                    <span className="mono">{family.id}</span>
                    <b>{concept.creatives.length}</b>
                  </button>
                </td>
                <td>
                  <strong className="concept-statement">{family.statement}</strong>
                  <div className="concept-tags">
                    <Tag tone="pain">{family.pain}</Tag><Tag tone="desire">{family.desire}</Tag><Tag>{family.angle}</Tag>
                    {/* Вариация в реестре — своя строка со своей экономикой (в кабинете
                        это отдельный концепт), поэтому родитель назван прямо здесь. */}
                    {family.parentId && <span className="lineage-chip" title={family.variationNote ?? `Вариация ${family.parentId}`}>
                      <GitBranch size={11} />{family.parentId}
                    </span>}
                  </div>
                </td>
                <td><StatusPill status={family.status} /></td>
                <td>
                  {concept.verdicts.length > 1
                    ? <span className="split-marker"><Split size={12} /> {concept.verdicts.length} разных</span>
                    : <VerdictPill verdict={concept.verdicts[0] ?? null} />}
                </td>
                <td>{formatMoney(concept.metrics?.spend ?? 0)}</td>
                <td>
                  {formatPercent(concept.metrics?.hookRate ?? null)}
                  {concept.metrics?.bestHookRate != null && concept.metrics.measuredCreatives > 1
                    && <span className="cell-best">бест {formatPercent(concept.metrics.bestHookRate)}</span>}
                </td>
                <td>{formatPercent(concept.metrics?.holdRate ?? null)}</td>
                <td>{formatPercent(concept.metrics?.ctr ?? null, 2)}</td>
                <td>
                  {formatMoney(concept.metrics?.cpa ?? null)}
                  {concept.metrics?.bestCpa != null && concept.metrics.measuredCreatives > 1
                    && <span className="cell-best">бест {formatMoney(concept.metrics.bestCpa)}</span>}
                </td>
                <td className="ad-cell">{family.priority} · {family.source}</td>
                <td><div className="row-actions">
                  <button onClick={() => navigate(`/hypotheses/${family.id}`)} aria-label={`Открыть ${family.id}`}><ExternalLink size={14} /></button>
                  <HypothesisControls family={family} />
                </div></td>
              </tr>
              {open && concept.creatives.map((creative) => <tr className="creative-row" key={creative.id}>
                <td><span className="creative-row__id mono">{creative.id}</span></td>
                <td>
                  <div className="creative-row__tags">
                    <span className={`producer producer--${creative.producer}`}>{creative.producer}</span>
                    <Tag>{creative.format}</Tag><Tag>{creative.hook}</Tag><Tag>{creative.pacing}</Tag>
                  </div>
                </td>
                <td><span className={`creative-state creative-state--${creative.state}`}>{creativeStateLabels[creative.state]}</span></td>
                <td><VerdictPill verdict={creative.verdict} /></td>
                <td>{formatMoney(creative.metrics.spend)}</td>
                <td>{formatPercent(creative.metrics.hookRate)}</td>
                <td>{formatPercent(creative.metrics.holdRate)}</td>
                <td>{formatPercent(creative.metrics.ctr, 2)}</td>
                <td>{formatMoney(creative.metrics.cpa)}</td>
                <td className="ad-cell" title={creative.adName}>{creative.adName}</td>
                <td><button onClick={() => navigate(`/hypotheses/${family.id}`)} aria-label={`Открыть ${creative.id}`}><ExternalLink size={14} /></button></td>
              </tr>)}
            </Fragment>;
          })}
          {!concepts.length && <tr><td colSpan={11}><div className="table-empty">Ничего не найдено. Сбросьте поиск или фильтр состояния.</div></td></tr>}
        </tbody>
      </table>
    </div>
  </>;
}
